import { useCallback } from 'react';
import type { TFunction } from 'i18next';
import { ASSIGNED_CONFIG, INTERACTED_CONFIG, JOB_OFFERS_CONFIG, TOTAL_UNIQUE_CANDIDATE_CONFIG } from './constans';
import type {
  AnalyticsAppliedFilters,
  AnalyticsChartTooltipContext,
  AnalyticsFilterListItem,
  AnalyticsTooltipChartType,
  AnalyticsTooltipOption,
  AnalyticsTooltipOptions,
  AnalyticsWidgetConfig,
  AssignedRawData,
  InteractedRawData,
  JobOffersRawData,
  PassingStepRawItem,
  RejectionNumberRawItem,
  RejectionReasonRawItem,
  TotalUniqueCandidatesRawData,
  TransitionStepRawResponse,
} from './types';
import './styles.scss';

export const AnalyticsFilterHelper = (resp: AnalyticsFilterListItem[]) =>
  resp.map(item => ({
    value: item.uuid,
    label: item.company_name ? `${item.name}  (${item.company_name})` : item.name,
  }));

export const rejectionNumberHelper = (resp: RejectionNumberRawItem[]) =>
  resp.map(item => ({
    name: item.type,
    value: item.rejections,
    data: [
      { name: 'Rejection:', data: item.rejections },
      { name: 'Absolute conversion:', data: `${item.absolute_conversion}%` },
    ],
  }));

export const rejectionReasonHelper = (resp: RejectionReasonRawItem[]) =>
  resp.map(item => ({
    type: item.type,
    reasons: item.reasons
      .filter(reason => !!reason.rejections)
      .sort((a, b) => b.rejections - a.rejections)
      .map(option => ({
        name: option.reason,
        value: option.rejections,
        data: [
          { name: 'Rejection:', data: option.rejections },
          { name: 'Absolute conversion:', data: `${option.absolute_conversion}%` },
        ],
      })),
  }));

interface TransitionStepOption {
  type: string;
  candidates_count: number;
  relative_conversion?: string;
  absolute_conversion?: string;
}

export const transitionStepHelper = (resp: TransitionStepRawResponse) => {
  const options: TransitionStepOption[] = resp.steps_transition
    .filter(item => !!item.candidates_count && item.type !== 'Technical Interview')
    .map(option => ({
      ...option,
      relative_conversion: option.relative_conversion ? `${option.relative_conversion}%` : '-',
      absolute_conversion: option.absolute_conversion ? `${option.absolute_conversion}%` : '-',
    }));

  options.unshift({
    type: 'Contacted',
    candidates_count: resp.interacted.new_interaction,
  });

  return {
    options,
    empty: !resp.steps_transition.filter(item => !!item.candidates_count).length,
  };
};

export const passingStepHelper = (resp: PassingStepRawItem[]) =>
  resp.map((item, index) => ({ label: item.type, value: item.type, data: item.duration_hours, index }));

export const interactedDataMapper = (data: InteractedRawData, t: TFunction): AnalyticsWidgetConfig => ({
  ...INTERACTED_CONFIG,
  total: data.new_interaction,
  extra: data.new_added,
  extra_description: t('translation:analytics_recruiters_info.text_new_added_candidates'),
  options: data.new_added
    ? [{ label: t('translation:analytics_recruiters_info.text_new_added_candidates'), value: data.new_added }]
    : [],
  loading: false,
});

export const assignedDataMapper = (
  data: AssignedRawData,
  { jobs, services }: Pick<AnalyticsAppliedFilters, 'jobs' | 'services'>,
  t: TFunction
): AnalyticsWidgetConfig => {
  const simpleView = (!jobs.length && services.length !== 1) || (jobs.length > 1 && services.length > 1);

  return {
    ...ASSIGNED_CONFIG,
    total: data.candidates,
    simpleView,
    options:
      jobs.length === 1
        ? []
        : simpleView
        ? [
            { label: t('translation:analytics_recruiters_info.text_services'), value: data.services },
            { label: t('translation:analytics_recruiters_info.text_jobs'), value: data.jobs.length },
          ]
        : data.jobs.map(job => ({ label: job.name, value: job.candidates_number })),
    loading: false,
  };
};

export const jobOffersDataMapper = (data: JobOffersRawData, t: TFunction): AnalyticsWidgetConfig => ({
  ...JOB_OFFERS_CONFIG,
  total: data.total,
  options: [
    { label: t('translation:analytics_recruiters_info.text_rejected'), value: data.rejected },
    { label: t('translation:analytics_recruiters_info.text_onboarding'), value: data.onboarding },
    { label: t('translation:analytics_recruiters_info.text_waiting_feedback'), value: data.waiting_feedback },
  ],
  loading: false,
});

export const totalUniqueCandidatesDataMapper = (
  data: TotalUniqueCandidatesRawData,
  t: TFunction
): AnalyticsWidgetConfig[] => [
  {
    ...TOTAL_UNIQUE_CANDIDATE_CONFIG,
    total: data.candidates,
    options: [
      { label: t('translation:analytics_team_leads_info.text_services'), value: data.services },
      { label: t('translation:analytics_team_leads_info.text_jobs'), value: data.jobs.length },
    ],
    loading: false,
  },
];

export const useAnalyticsTooltip = (options: AnalyticsTooltipOptions, type?: AnalyticsTooltipChartType) =>
  useCallback(
    (context: AnalyticsChartTooltipContext) => {
      const { chart, tooltip } = context;
      let tooltipEl = chart.canvas.parentNode.querySelector('div.tooltipDesign') as HTMLDivElement | null;
      let tooltipUl = chart.canvas.parentNode.querySelector('ul') as HTMLUListElement | null;
      if (!tooltipEl || !tooltipUl) {
        tooltipEl = document.createElement('div');
        tooltipEl.classList.add('tooltipDesign');
        tooltipUl = document.createElement('ul');
        tooltipUl.classList.add('tooltipUl');

        tooltipEl.appendChild(tooltipUl);
        chart.canvas.parentNode.appendChild(tooltipEl);
      }

      if (tooltip.opacity === 0) {
        tooltipEl.style.display = 'none';
        return;
      }

      if (tooltip.body) {
        let tooltipOptions: AnalyticsTooltipOption[] = Array.isArray(options) ? options : options.bar;
        if (!Array.isArray(options)) {
          if (tooltip.body[0].lines[0].includes('Step time')) tooltipOptions = options.bar;
          if (tooltip.body[0].lines[0].includes('Total time')) tooltipOptions = options.line;
        }
        let values: AnalyticsTooltipOption['data'] | undefined;
        if (type === 'doughnut') {
          values = tooltipOptions.find(option => option.name === tooltip.dataPoints[0].label)?.data;
        } else {
          values = tooltipOptions.find(option => option.name === tooltip.title[0].replace(',', ' '))?.data;
        }

        if (!values) return;

        const tooltipLi = document.createElement('li');
        const leftBlock = document.createElement('div');
        leftBlock.classList.add('left');
        const rightBlock = document.createElement('div');
        rightBlock.classList.add('right');
        tooltipLi.appendChild(leftBlock);
        tooltipLi.appendChild(rightBlock);
        values.forEach(option => {
          const leftSpan = document.createElement('span');
          const leftText = document.createTextNode(option.name);
          leftSpan.appendChild(leftText);
          leftBlock.appendChild(leftSpan);
          const rightSpan = document.createElement('span');
          const rightText = document.createTextNode(String(option.data));
          rightSpan.appendChild(rightText);
          rightBlock.appendChild(rightSpan);
        });

        while (tooltipUl.firstChild) {
          tooltipUl.firstChild.remove();
        }

        tooltipUl.appendChild(tooltipLi);
        tooltipEl.style.display = 'block';

        const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;
        if (type === 'doughnut') {
          tooltipEl.style.left = `${positionX + 190}px`;
          tooltipEl.style.top = `${positionY + 65}px`;
        } else {
          tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
          tooltipEl.style.top = `${positionY - 110 + tooltip.caretY}px`;
        }
      }
    },
    [options, type]
  );
